<template>
  <div class="bonus-apply-container">
    <div class="page-header">
      <h2 class="page-title">综合素质分申请</h2>
      <el-button 
        type="primary" 
        icon="el-icon-plus" 
        @click="addNewApplication"
        class="add-button">
        新增申请
      </el-button>
    </div>

    <div class="page-content">
      <div class="form-description">
        <i class="el-icon-info"></i> 
        请填写综合素质分申请信息，可添加多项申请一次性提交。选择加分类别后会显示推荐分值，上传证明材料后方可提交。
      </div>
      
      <el-form ref="bonusForm" :model="formData" :rules="rules">
        <div v-for="(form, index) in applicationForms" :key="index" class="application-item">
          <div class="application-header">
            <span class="application-title">申请项 #{{ index + 1 }}</span>
            <el-button 
              type="danger" 
              icon="el-icon-delete" 
              circle 
              size="mini" 
              @click="removeApplication(index)"
              :disabled="applicationForms.length <= 1"
              class="delete-button">
            </el-button>
          </div>
          
          <div class="application-content">
            <div class="form-row">
              <div class="form-col">
                <label class="form-label">加分类别</label>
                <el-form-item 
                  :prop="'applicationForms.' + index + '.category_id'"
                  :rules="rules.category_id"
                  class="no-margin">
                  <el-cascader
                    v-model="form.category_id"
                    :options="categoryOptions"
                    :props="{ 
                      checkStrictly: true,
                      label: 'category_name',
                      value: 'category_id',
                      children: 'children'
                    }"
                    @change="(val) => handleCategoryChange(val, index)"
                    placeholder="请选择加分类别"
                    clearable
                    class="full-width">
                  </el-cascader>
                  <div class="category-info" v-if="form.description || form.recommendScore">
                    <el-tooltip 
                      v-if="form.description" 
                      effect="light" 
                      placement="top-start">
                      <div slot="content" v-html="form.description"></div>
                      <i class="el-icon-question info-icon"></i>
                    </el-tooltip>
                    <span class="recommend-score" v-if="form.recommendScore">
                      推荐分值: {{ form.recommendScore }}
                    </span>
                  </div>
                </el-form-item>
              </div>
              
              <div class="form-col">
                <label class="form-label">加分项目</label>
                <el-form-item 
                  :prop="'applicationForms.' + index + '.bonus_item'"
                  :rules="rules.bonus_item"
                  class="no-margin">
                  <el-input 
                    v-model="form.bonus_item" 
                    placeholder="请输入加分项目名称">
                  </el-input>
                </el-form-item>
              </div>
            </div>
            
            <div class="form-row">
              <div class="form-col">
                <label class="form-label">级别</label>
                <el-form-item 
                  :prop="'applicationForms.' + index + '.level'"
                  :rules="rules.level"
                  class="no-margin">
                  <el-select 
                    v-model="form.level" 
                    placeholder="请选择级别"
                    class="full-width">
                    <el-option label="国家级" value="国家级"></el-option>
                    <el-option label="省级" value="省级"></el-option>
                    <el-option label="市级" value="市级"></el-option>
                    <el-option label="校级" value="校级"></el-option>
                    <el-option label="院级" value="院级"></el-option>
                    <el-option label="其他" value="其他"></el-option>
                  </el-select>
                </el-form-item>
              </div>
              
              <div class="form-col">
                <label class="form-label">申请加分</label>
                <el-form-item 
                  :prop="'applicationForms.' + index + '.applied_bonus_points'"
                  :rules="rules.applied_bonus_points"
                  class="no-margin">
                  <el-input-number 
                    v-model="form.applied_bonus_points" 
                    :min="0" 
                    :max="100" 
                    :precision="2"
                    :step="0.5"
                    class="full-width">
                  </el-input-number>
                </el-form-item>
              </div>
            </div>
            
            <div class="form-row">
              <div class="form-col full-width">
                <label class="form-label">证明材料</label>
                <el-form-item 
                  :prop="'applicationForms.' + index + '.file'"
                  :rules="rules.file"
                  class="no-margin">
                  <el-upload
                    class="upload-box"
                    :action="api.uploadBonusFile"
                    :headers="uploadHeaders"
                    :on-preview="handlePreview"
                    :on-remove="(file, fileList) => handleRemove(file, fileList, index)"
                    :on-success="(response, file, fileList) => handleUploadSuccess(response, file, fileList, index)"
                    :on-error="handleUploadError"
                    :before-upload="beforeUpload"
                    :limit="1"
                    :file-list="form.fileList">
                    <el-button type="primary" plain>
                      <i class="el-icon-upload2"></i> 上传文件
                    </el-button>
                    <div class="el-upload__tip">支持jpg/png/pdf/doc格式，不超过10MB</div>
                  </el-upload>
                </el-form-item>
              </div>
            </div>
            
            <div class="form-row">
              <div class="form-col full-width">
                <label class="form-label">申请理由</label>
                <el-form-item 
                  :prop="'applicationForms.' + index + '.reason'"
                  :rules="rules.reason"
                  class="no-margin">
                  <el-input 
                    type="textarea" 
                    v-model="form.reason" 
                    :rows="3"
                    placeholder="请详细描述申请理由">
                  </el-input>
                </el-form-item>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 提交按钮 -->
        <div class="form-actions">
          <el-button type="primary" @click="submitForm" :loading="submitting" icon="el-icon-check">提交申请</el-button>
          <el-button @click="resetForm" icon="el-icon-refresh-left">重置表单</el-button>
        </div>
      </el-form>
    </div>
  </div>
</template>

<script>
import axiosInstance from "../../api/axiosInstance";
import api from "../../api/index";

export default {
  name: 'BounsApply',
  data() {
    return {
      api,
      categoryOptions: [],
      applicationForms: [{
        category_id: null,
        bonus_item: '',
        level: '',
        applied_bonus_points: 0,
        reason: '',
        file: null,
        fileList: [],
        recommendScore: null,
        description: ''
      }],
      formData: {
        applicationForms: []
      },
      rules: {
        category_id: [
          { required: true, message: '请选择加分类别', trigger: 'change' }
        ],
        bonus_item: [
          { required: true, message: '请输入加分项目', trigger: 'blur' },
          { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
        ],
        level: [
          { required: true, message: '请选择级别', trigger: 'change' }
        ],
        applied_bonus_points: [
          { required: true, message: '请输入申请加分', trigger: 'blur' }
        ],
        file: [
          { required: true, message: '请上传证明材料', trigger: 'change' }
        ],
        reason: [
          { required: true, message: '请输入申请理由', trigger: 'blur' },
          { min: 5, max: 500, message: '长度在 5 到 500 个字符', trigger: 'blur' }
        ]
      },
      submitting: false,
      uploadHeaders: {
        Authorization: localStorage.getItem('token') || ''
      }
    };
  },
  created() {
    this.formData.applicationForms = this.applicationForms;
    this.fetchCategories();
  },
  methods: {
    // 获取加分类别
    async fetchCategories() {
      try {
        const response = await axiosInstance.get(api.getBonusCategories);
        if (response.data.status === 'success') {
          this.categoryOptions = response.data.data;
        } else {
          this.$message.error(response.data.message || '获取加分类别失败');
        }
      } catch (error) {
        console.error('获取加分类别失败:', error);
        this.$message.error('获取加分类别失败');
      }
    },
    
    // 添加新申请
    addNewApplication() {
      this.applicationForms.push({
        category_id: null,
        bonus_item: '',
        level: '',
        applied_bonus_points: 0,
        reason: '',
        file: null,
        fileList: [],
        recommendScore: null,
        description: ''
      });
      this.formData.applicationForms = this.applicationForms;
    },
    
    // 移除申请
    removeApplication(index) {
      this.applicationForms.splice(index, 1);
      this.formData.applicationForms = this.applicationForms;
    },
    
    // 处理类别变化
    async handleCategoryChange(value, index) {
      if (!value || value.length === 0) {
        this.applicationForms[index].recommendScore = null;
        this.applicationForms[index].description = '';
        return;
      }
      
      const categoryId = value[value.length - 1];
      
      try {
        const response = await axiosInstance.get(api.getBonusCategoryDetail, {
          params: { category_id: categoryId }
        });
        
        if (response.data.status === 'success') {
          const category = response.data.data;
          this.applicationForms[index].recommendScore = category.recommend_score;
          this.applicationForms[index].description = category.description;
          this.applicationForms[index].applied_bonus_points = category.recommend_score;
        }
      } catch (error) {
        console.error('获取类别详情失败:', error);
      }
    },
    
    // 文件上传前的验证
    beforeUpload(file) {
      const isValidType = ['image/jpeg', 'image/png', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(file.type);
      const isLt10M = file.size / 1024 / 1024 < 10;
      
      if (!isValidType) {
        this.$message.error('上传文件只能是 JPG/PNG/PDF/DOC/DOCX 格式!');
        return false;
      }
      
      if (!isLt10M) {
        this.$message.error('上传文件大小不能超过 10MB!');
        return false;
      }
      
      return true;
    },
    
    // 处理文件上传成功
    handleUploadSuccess(response, file, fileList, index) {
      if (response.status === 'success') {
        this.applicationForms[index].file = response.data.url;
        this.$message.success('文件上传成功');
      } else {
        this.$message.error(response.message || '文件上传失败');
        fileList.pop();
      }
    },
    
    // 处理文件上传失败
    handleUploadError(err) {
      console.error('上传失败:', err);
      this.$message.error('文件上传失败');
    },
    
    // 处理文件移除
    handleRemove(file, fileList, index) {
      this.applicationForms[index].file = null;
    },
    
    // 处理文件预览
    handlePreview(file) {
      window.open(file.url);
    },
    
    // 提交表单
    submitForm() {
      this.$refs.bonusForm.validate(async (valid) => {
        if (!valid) {
          this.$message.warning('请完善表单信息');
          return;
        }
        
        // 检查每个申请是否都上传了文件
        for (let i = 0; i < this.applicationForms.length; i++) {
          if (!this.applicationForms[i].file) {
            this.$message.warning(`申请项 #${i + 1} 未上传证明材料`);
            return;
          }
        }
        
        this.submitting = true;
        
        try {
          // 逐个提交申请
          for (const form of this.applicationForms) {
            const applicationData = {
              category_id: Array.isArray(form.category_id) ? form.category_id[form.category_id.length - 1] : form.category_id,
              bonus_item: form.bonus_item,
              level: form.level,
              applied_bonus_points: form.applied_bonus_points,
              url: form.file,
              reason: form.reason
            };
            
            const response = await axiosInstance.post(api.submitBonusApplication, applicationData);
            
            if (response.data.status !== 'success') {
              throw new Error(response.data.message || '提交申请失败');
            }
          }
          
          this.$message.success('申请提交成功');
          this.resetForm();
        } catch (error) {
          console.error('提交申请失败:', error);
          this.$message.error(error.message || '申请提交失败');
        } finally {
          this.submitting = false;
        }
      });
    },
    
    // 重置表单
    resetForm() {
      this.applicationForms = [{
        category_id: null,
        bonus_item: '',
        level: '',
        applied_bonus_points: 0,
        reason: '',
        file: null,
        fileList: [],
        recommendScore: null,
        description: ''
      }];
      this.formData.applicationForms = this.applicationForms;
      if (this.$refs.bonusForm) {
        this.$refs.bonusForm.clearValidate();
      }
    }
  }
};
</script>

<style scoped>
.bonus-apply-container {
  padding: 20px;
  max-width: 1000px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-title {
  font-size: 22px;
  color: #303133;
  margin: 0;
  font-weight: 500;
}

.add-button {
  padding: 10px 20px;
  font-weight: 500;
}

.page-content {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  padding: 20px;
}

.form-description {
  background-color: #ecf8ff;
  padding: 12px 16px;
  border-radius: 4px;
  border-left: 5px solid #50bfff;
  margin-bottom: 20px;
  color: #5e6d82;
  line-height: 1.5;
  font-size: 14px;
}

.application-item {
  background: #f9f9f9;
  border-radius: 6px;
  margin-bottom: 20px;
  border: 1px solid #ebeef5;
  overflow: hidden;
}

.application-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
}

.application-title {
  font-size: 16px;
  font-weight: 500;
  color: #606266;
}

.delete-button {
  margin-left: auto;
}

.application-content {
  padding: 20px;
}

.form-row {
  display: flex;
  margin-bottom: 20px;
  gap: 20px;
}

.form-col {
  flex: 1;
}

.full-width {
  width: 100%;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: #606266;
  font-weight: 500;
}

.no-margin {
  margin-bottom: 0;
}

.category-info {
  display: flex;
  align-items: center;
  margin-top: 5px;
}

.info-icon {
  color: #409EFF;
  cursor: pointer;
}

.recommend-score {
  color: #409EFF;
  font-size: 12px;
  margin-left: 10px;
}

.upload-box {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  padding: 20px;
  text-align: center;
  background: #fafafa;
  transition: border-color 0.3s;
}

.upload-box:hover {
  border-color: #409EFF;
}

.el-upload__tip {
  line-height: 1.4;
  font-size: 12px;
  color: #909399;
  margin-top: 10px;
}

.form-actions {
  text-align: center;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
}

.form-actions .el-button {
  padding: 12px 25px;
  font-size: 14px;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
  }
  
  .form-col {
    margin-bottom: 15px;
  }
}
</style>
